
import { CHROME_ASSETS, elem, installChrome, makeDraggable, styleInstaller } from "./chrome";
import { wornIn } from "../wardrobe";
import { itemArtNode } from "./itemart";
import type { BagSlotModel } from "./bag";
import { ItemTooltip, attachHoverTooltip, slotTooltipData, type ItemTooltipData } from "./tooltip";

export const COSTUME_WINDOW_WIDTH = 140;
export const COSTUME_WINDOW_HEIGHT = 180;
export const COSTUME_TITLE = "Costumes";

/** `WEAR_COSTUME_BODY` / `WEAR_COSTUME_HAIR` (`length.h:125-126`). */
export const COSTUME_BODY_WEAR = 19;
export const COSTUME_HAIR_WEAR = 20;

/** Slot geometry inside the 140×180 board, from `costumewindow.py:73-75`. */
export const COSTUME_SLOTS: readonly {
  readonly wear: number;
  readonly label: string;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}[] = [
  { wear: COSTUME_HAIR_WEAR, label: "Hair", left: 77, top: 49, width: 32, height: 32 },
  { wear: COSTUME_BODY_WEAR, label: "Body", left: 77, top: 86, width: 32, height: 64 },
];

export interface CostumeActions {
  readonly onUnequip?: (wear: number) => void;
}

const STYLE_ID = "costume-window-style";
const STYLE = `
.costume-window {
  position: fixed;
  right: 175px;
  bottom: 37px;
  z-index: 55;
  width: ${COSTUME_WINDOW_WIDTH}px;
  height: ${COSTUME_WINDOW_HEIGHT}px;
  pointer-events: auto;
}
.costume-window-title { pointer-events: auto; }
.costume-window-base {
  position: absolute;
  left: 13px;
  top: 38px;
  width: 114px;
  height: 132px;
  background: url("/ui/game/costume_bg.jpg") center / cover no-repeat;
}
.costume-slot {
  position: absolute;
  box-sizing: border-box;
  border: 1px solid #5a4a32;
  background: url("${CHROME_ASSETS.slotBase}") center / 32px 32px no-repeat;
  cursor: pointer;
  padding: 0;
}
.costume-slot .item-icon { max-width: 100%; max-height: 100%; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

function tooltipData(slot: BagSlotModel): ItemTooltipData {
  return slotTooltipData(slot, { price: slot.sellValue });
}

export class CostumeWindow {
  readonly element: HTMLElement;
  private readonly cells: HTMLButtonElement[] = [];
  private readonly tooltip: ItemTooltip;
  private lastWorn: readonly BagSlotModel[] = [];

  constructor(mount: HTMLElement, private readonly actions: CostumeActions = {}) {
    const doc = mount.ownerDocument;
    installChrome(doc);
    installStyle(doc);
    this.tooltip = new ItemTooltip(doc);

    this.element = elem(doc, "section", "costume-window m2-board");
    this.element.hidden = true;
    this.element.setAttribute("role", "dialog");
    this.element.setAttribute("aria-label", COSTUME_TITLE);

    const title = elem(doc, "div", "m2-titlebar costume-window-title", COSTUME_TITLE);
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.close());
    makeDraggable(this.element, title);

    const base = elem(doc, "div", "costume-window-base");
    for (const spec of COSTUME_SLOTS) {
      const cell = doc.createElement("button");
      cell.type = "button";
      cell.className = "costume-slot";
      cell.dataset.wear = String(spec.wear);
      cell.style.left = `${spec.left - 13}px`;
      cell.style.top = `${spec.top - 38}px`;
      cell.style.width = `${spec.width}px`;
      cell.style.height = `${spec.height}px`;
      cell.dataset.label = spec.label;
      cell.setAttribute("aria-label", spec.label);
      cell.addEventListener("click", () => this.actions.onUnequip?.(spec.wear));
      attachHoverTooltip(cell, this.tooltip, () => {
        const worn = wornIn(this.lastWorn, spec.wear);
        return worn === undefined ? null : tooltipData(worn);
      });
      this.cells.push(cell);
      base.append(cell);
    }

    this.element.append(title, close, base);
    mount.append(this.element);
  }

  get showing(): boolean {
    return !this.element.hidden;
  }

  toggle(): void {
    this.element.hidden = !this.element.hidden;
  }

  close(): void {
    this.element.hidden = true;
  }

  update(worn: readonly BagSlotModel[]): void {
    this.lastWorn = worn;
    for (const cell of this.cells) {
      const wear = Number(cell.dataset.wear);
      const slot = wornIn(worn, wear);
      cell.replaceChildren();
      cell.setAttribute("aria-label", slot === undefined ? cell.dataset.label ?? "" : slot.name);
      cell.title = slot?.name ?? "";
      if (slot !== undefined) {
        cell.append(itemArtNode(slot.vnum, slot.kind, this.element.ownerDocument));
      }
    }
  }
}
